/* eslint-disable no-console */
import { useState } from 'react';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { IPaginator } from 'models/paginator';
import { EPerPages, ISortOptions } from 'models/table';
import { EAlertVariants } from 'components/atoms/Alert';
import { IUser } from 'models/user';
import { useDictionary } from 'models/dictionaryContext';
import {
  createUser,
  deleteUser,
  generatePassword,
  getUsers,
  resetOfflineAuthenticator,
  setUserActive,
  setUserBlocked,
  updateUser,
} from 'requests/user';
import { ISearchUserFormValues, IUserFormValues } from 'models/form';
import { useTranslations } from 'hooks/useTranslations';
import { LEGAL_ENTITY_GROUP_MAPPING } from './constants';

export const useUsers = () => {
  const { t } = useTranslations();
  const [users, setUsers] = useState<IUser[]>([]);
  const [paginator, setPaginator] = useState<IPaginator | null>(null);
  const [assignedLEs, setAssignedLEs] = useState<any[]>([]);

  const { setAlert } = useAlert();
  const {
    getPhoneCode,
    getLegalEntityPrefix,
    getOrganisationCategory,
    getWeaponLicencingAuthority,
  } = useDictionary();

  const getUsersData = async (
    currentPage: number = 1,
    perPage: number | EPerPages = EPerPages.perPage25,
    query: Partial<ISearchUserFormValues> = {},
    sort?: ISortOptions | null,
  ) => {
    const usersData = await getUsers(currentPage, perPage, query, sort);

    if (usersData) {
      const { content, ...paginatorData } = usersData;
      setUsers(content);
      setPaginator(paginatorData);
    } else {
      setUsers([]);
      setPaginator(null);
    }
  };

  const loadDictionaries = async () => {
    getPhoneCode();
    getLegalEntityPrefix();
    getOrganisationCategory();
    getWeaponLicencingAuthority();
  };

  const activateUser = async (id: string, active: boolean) => {
    const result = await setUserActive(id, active);
    if (result) {
      setUsers((prevState) =>
        prevState.map((item) => {
          if (item.uniqueId === id) {
            item.status = active;
          }
          return item;
        }),
      );
    }

    return result;
  };

  const unbanUser = async (id: string) => {
    const result = await setUserBlocked(id);
    if (result) {
      setUsers((prevState) =>
        prevState.map((item) => {
          if (item.uniqueId === id) {
            item.blocked = false;
          }
          return item;
        }),
      );
    }
  };

  const handleCreate = async (data: IUserFormValues) => {
    data.password = `Fake!${Math.floor(Math.random() * 10000)}!Password`;

    if (data.formRoleIds) {
      data.roleIds = Object.keys(data.formRoleIds).filter(
        (key) => data?.formRoleIds?.[key] === true,
      );
    }

    // Process Legal Entity Group checkboxes using mapping
    const prefixGroupIds: string[] = [];
    const organisationCategoryIds: string[] = [];

    if (data.formPrefixGroupIds) {
      const checkedPrefixIds = Object.keys(data.formPrefixGroupIds).filter(
        (key) => data?.formPrefixGroupIds?.[key] === true,
      );

      checkedPrefixIds.forEach((id) => {
        const mapping = LEGAL_ENTITY_GROUP_MAPPING[id];
        if (mapping) {
          if (mapping.prefixGroupId) {
            prefixGroupIds.push(mapping.prefixGroupId);
          }
          if (mapping.organisationCategoryId) {
            organisationCategoryIds.push(mapping.organisationCategoryId);
          }
        }
      });
    }

    if (data.formOrganisationCategoryIds) {
      const checkedOrgCategoryIds = Object.keys(data.formOrganisationCategoryIds).filter(
        (key) => data?.formOrganisationCategoryIds?.[key] === true,
      );

      checkedOrgCategoryIds.forEach((id) => {
        const mapping = LEGAL_ENTITY_GROUP_MAPPING[id];
        if (mapping) {
          if (mapping.prefixGroupId && !prefixGroupIds.includes(mapping.prefixGroupId)) {
            prefixGroupIds.push(mapping.prefixGroupId);
          }
          if (
            mapping.organisationCategoryId &&
            !organisationCategoryIds.includes(mapping.organisationCategoryId)
          ) {
            organisationCategoryIds.push(mapping.organisationCategoryId);
          }
        }
      });
    }

    data.prefixGroupIds = prefixGroupIds;
    data.organisationCategoryIds = organisationCategoryIds;

    const {
      formRoleIds,
      formPrefixGroupIds,
      formOrganisationCategoryIds,
      formWeaponLicencingAuthority,
      phoneNumberCodeDictionaryUniqueId,
      phoneNumberValue,
      ...rest
    } = data;
    const phoneNumber = {
      codeDictionaryUniqueId: phoneNumberCodeDictionaryUniqueId,
      value: phoneNumberValue,
    };
    const response = await createUser({ ...rest, phoneNumber });
    if (response.status === 200) {
      await setAlert(ActionTypes.SET_NEXT_PAGE_ALERT, { text: t('addUser.success.text') });
    } else if (response.status === 400) {
      await setAlert(ActionTypes.SET_USER_ALERT, {
        text: t('addUser.emailIsTaken.message'),
        variant: EAlertVariants.error,
      });
    }

    return response;
  };

  const handleEdit = async (id: string, data: IUserFormValues) => {
    if (data.formRoleIds) {
      data.roleIds = Object.keys(data.formRoleIds).filter(
        (key) => data?.formRoleIds?.[key] === true,
      );
    }

    // Process Legal Entity Group checkboxes using mapping
    const prefixGroupIds: string[] = [];
    const organisationCategoryIds: string[] = [];

    if (data.formPrefixGroupIds) {
      const checkedPrefixIds = Object.keys(data.formPrefixGroupIds).filter(
        (key) => data?.formPrefixGroupIds?.[key] === true,
      );

      checkedPrefixIds.forEach((checkboxId) => {
        const mapping = LEGAL_ENTITY_GROUP_MAPPING[checkboxId];
        if (mapping) {
          if (mapping.prefixGroupId) {
            prefixGroupIds.push(mapping.prefixGroupId);
          }
          if (mapping.organisationCategoryId) {
            organisationCategoryIds.push(mapping.organisationCategoryId);
          }
        }
      });
    }

    if (data.formOrganisationCategoryIds) {
      const checkedOrgCategoryIds = Object.keys(data.formOrganisationCategoryIds).filter(
        (key) => data?.formOrganisationCategoryIds?.[key] === true,
      );

      checkedOrgCategoryIds.forEach((checkboxId) => {
        const mapping = LEGAL_ENTITY_GROUP_MAPPING[checkboxId];
        if (mapping) {
          if (mapping.prefixGroupId && !prefixGroupIds.includes(mapping.prefixGroupId)) {
            prefixGroupIds.push(mapping.prefixGroupId);
          }
          if (
            mapping.organisationCategoryId &&
            !organisationCategoryIds.includes(mapping.organisationCategoryId)
          ) {
            organisationCategoryIds.push(mapping.organisationCategoryId);
          }
        }
      });
    }

    data.prefixGroupIds = prefixGroupIds;
    data.organisationCategoryIds = organisationCategoryIds;

    const {
      formRoleIds,
      formPrefixGroupIds,
      formOrganisationCategoryIds,
      formWeaponLicencingAuthority,
      phoneNumberCodeDictionaryUniqueId,
      phoneNumberValue,
      password,
      ...rest
    } = data;
    const phoneNumber = {
      codeDictionaryUniqueId: phoneNumberCodeDictionaryUniqueId,
      value: phoneNumberValue,
    };
    const response = await updateUser(id, { ...rest, phoneNumber });
    if (response) {
      await setAlert(ActionTypes.SET_USER_ALERT, { text: t('editUser.success.text') });
    }
    return response;
  };

  const handleRegeneratePassword = async (email: string) => {
    const response = await generatePassword(email);
    return response;
  };

  const handleDelete = async (id: string) => {
    const response = await deleteUser(id);
    if (response) {
      await setAlert(ActionTypes.SET_USER_ALERT, { text: t('users.delete.success.message') });
    }
    return response;
  };

  const handleResetOfflineAuthenticator = async (id: string) => {
    const response = await resetOfflineAuthenticator(id);
    if (response) {
      await setAlert(ActionTypes.SET_USER_ALERT, {
        text: t('users.resetOfflineAuthenticator.success.message'),
      });
    }
    return response;
  };

  const getAssignedLEData = async () => {
    // const assignedLEData = await getAssignedLEs();
    const assignedLEData: any = [];
    if (assignedLEData) {
      setAssignedLEs(assignedLEData);
    } else {
      setAssignedLEs([]);
    }
  };

  return {
    loadDictionaries,
    alert,
    users,
    paginator,
    handleCreate,
    handleEdit,
    handleDelete,
    handleRegeneratePassword,
    getUsersData,
    activateUser,
    unbanUser,
    getAssignedLEData,
    assignedLEs,
    handleResetOfflineAuthenticator,
  };
};
